// pages/AdminPage.jsx - Admin Dashboard for Connection Management
import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { 
  Loader2, Check, X, ExternalLink, RefreshCw, 
  Link2, AlertCircle, Settings, FileSpreadsheet,
  FolderOpen, Plus, Copy, Trash2
} from 'lucide-react'
import * as api from '../services/api'

export default function AdminPage() {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tenant, setTenant] = useState(null)
  const [formLinks, setFormLinks] = useState([])
  const [stats, setStats] = useState(null)
  const [refreshing, setRefreshing] = useState(false)
  
  // Get tenant ID from URL or localStorage
  const tenantId = searchParams.get('tenantId') || localStorage.getItem('pesticideTenantId')
  
  // Check for OAuth callback results
  const connected = searchParams.get('connected')
  const oauthError = searchParams.get('error')
  
  // Save tenant ID to localStorage
  useEffect(() => {
    if (tenantId) {
      localStorage.setItem('pesticideTenantId', tenantId)
    }
  }, [tenantId])
  
  // Load tenant data
  useEffect(() => {
    if (tenantId) {
      loadTenantData()
    } else {
      setLoading(false)
    }
  }, [tenantId])
  
  const loadTenantData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [statusResult, linksResult, statsResult] = await Promise.all([
        api.getConnectionStatus(tenantId),
        api.getFormLinks(tenantId, { limit: 10 }),
        api.getFormLinkStats(tenantId)
      ])
      
      setTenant(statusResult)
      setFormLinks(linksResult.formLinks || [])
      setStats(statsResult.stats || {})
      
    } catch (err) {
      console.error('Failed to load tenant data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }
  
  // Connect to Jobber
  const connectJobber = () => {
    const apiBase = import.meta.env.VITE_API_URL || '';
    console.log('VITE_API_URL:', import.meta.env.VITE_API_URL);
    console.log('Redirecting to:', `${apiBase}/oauth/connect`);
    window.location.href = `${apiBase}/oauth/connect`
  }
  
  // Connect to Google
  const connectGoogle = () => {
    if (!tenantId) {
      setError('Please connect to Jobber first')
      return
    }
    const apiBase = import.meta.env.VITE_API_URL || '';
    window.location.href = `${apiBase}/oauth/google/connect?tenantId=${tenantId}`
  }
  
  // Disconnect Jobber
  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect? This will revoke all active form links.')) {
      return
    }

    try {
      await api.disconnect(tenantId)
      localStorage.removeItem('pesticideTenantId')
      setTenant(null)
      setFormLinks([])
      setStats(null)
    } catch (err) {
      setError(err.message)
    }
  }

  // Disconnect Google
  const handleGoogleDisconnect = async () => {
    if (!confirm('Disconnect Google integration? You can reconnect anytime using the same spreadsheet.')) {
      return
    }

    try {
      await api.disconnectGoogle(tenantId)
      // Reload tenant data to reflect disconnected state
      await loadTenantData()
    } catch (err) {
      setError(err.message)
    }
  }

  // Refresh cache
  const handleRefreshCache = async () => {
    if (!tenantId) return

    setRefreshing(true)
    setError(null)

    try {
      await api.refreshCacheAdmin(tenantId)
      console.log('✅ Cache refreshed successfully')
    } catch (err) {
      console.error('Failed to refresh cache:', err)
      setError('Failed to refresh data. Please try again.')
    } finally {
      setRefreshing(false)
    }
  }

  // Create manual form link
  const createManualLink = async () => {
    const jobNumber = prompt('Enter Job Number (optional):')
    const clientName = prompt('Enter Client Name (optional):')
    
    try {
      const result = await api.createFormLink(tenantId, { jobNumber, clientName })
      setFormLinks(prev => [result.formLink, ...prev])
    } catch (err) {
      setError(err.message)
    }
  }
  
  // Copy form link
  const copyLink = (shortCode) => {
    const url = `${window.location.origin}/form/${shortCode}`
    navigator.clipboard.writeText(url)
    alert('Link copied to clipboard!')
  }
  
  // Revoke form link
  const revokeLink = async (shortCode) => {
    if (!confirm('Revoke this form link?')) return
    
    try {
      await api.revokeFormLink(shortCode)
      setFormLinks(prev => prev.map(l => 
        l.short_code === shortCode ? { ...l, status: 'revoked' } : l
      ))
    } catch (err) {
      setError(err.message)
    }
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-arbor-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-arbor-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Pesticide Records</h1>
              <p className="text-arbor-100 mt-1">Admin Dashboard</p>
            </div>
            <Settings className="w-6 h-6 text-arbor-200" />
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* OAuth callback messages */}
        {connected && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-800">
              Successfully connected to {connected === 'google' ? 'Google' : 'Jobber'}!
            </span>
          </div>
        )}
        
        {oauthError && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">
              Connection failed: {oauthError.replace(/_/g, ' ')}
            </span>
          </div>
        )}
        
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}

        {/* Google needs re-auth warning */}
        {tenant?.google?.needsReauth && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <span className="text-amber-800 font-medium">Google connection expired</span>
                <p className="text-amber-700 text-sm mt-1">
                  Your Google authorization has expired. Please reconnect to continue storing records.
                </p>
              </div>
              <button
                onClick={connectGoogle}
                className="btn-primary text-sm py-2 px-4 flex-shrink-0"
              >
                Reconnect
              </button>
            </div>
          </div>
        )}
        
        {/* Connection Status */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">Connections</h2>
          
          <div className="space-y-4">
            {/* Jobber Connection */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  tenant?.jobber?.connected ? 'bg-green-100' : 'bg-gray-200'
                }`}>
                  <Link2 className={`w-5 h-5 ${
                    tenant?.jobber?.connected ? 'text-green-600' : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <div className="font-medium">Jobber</div>
                  <div className="text-sm text-gray-500">
                    {tenant?.jobber?.connected 
                      ? `Connected as ${tenant.companyName || 'Unknown'}` 
                      : 'Not connected'}
                  </div>
                </div>
              </div>
              
              {tenant?.jobber?.connected ? (
                <button
                  onClick={handleDisconnect}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Disconnect
                </button>
              ) : (
                <button
                  onClick={connectJobber}
                  className="btn-primary text-sm py-2 px-4"
                >
                  Connect
                </button>
              )}
            </div>
            
            {/* Google Connection */}
            <div className={`flex items-center justify-between p-4 rounded-lg ${
              tenant?.google?.needsReauth ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  tenant?.google?.connected ? 'bg-green-100' :
                  tenant?.google?.needsReauth ? 'bg-amber-100' : 'bg-gray-200'
                }`}>
                  <FileSpreadsheet className={`w-5 h-5 ${
                    tenant?.google?.connected ? 'text-green-600' :
                    tenant?.google?.needsReauth ? 'text-amber-600' : 'text-gray-400'
                  }`} />
                </div>
                <div>
                  <div className="font-medium">Google Sheets & Drive</div>
                  <div className="text-sm text-gray-500">
                    {tenant?.google?.connected
                      ? 'Connected - Records & photos storage ready'
                      : tenant?.google?.needsReauth
                      ? 'Connection expired - reconnect to continue'
                      : 'Required for storing records'}
                  </div>
                </div>
              </div>

              {tenant?.google?.connected ? (
                <div className="flex items-center gap-3">
                  <a
                    href={`https://docs.google.com/spreadsheets/d/${tenant.google.sheetId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-arbor-600 hover:text-arbor-700 flex items-center gap-1"
                  >
                    Open Sheet
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <button
                    onClick={handleGoogleDisconnect}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Disconnect
                  </button>
                </div>
              ) : tenant?.google?.needsReauth ? (
                <button
                  onClick={connectGoogle}
                  className="btn-primary text-sm py-2 px-4"
                >
                  Reconnect
                </button>
              ) : (
                <button
                  onClick={connectGoogle}
                  disabled={!tenant?.jobber?.connected}
                  className="btn-primary text-sm py-2 px-4 disabled:opacity-50"
                >
                  Connect
                </button>
              )}
            </div>
          </div>
          
          {!tenant?.jobber?.connected && (
            <p className="mt-4 text-sm text-gray-500 text-center">
              Connect to Jobber to get started. Form links will be automatically created for new jobs.
            </p>
          )}
        </div>
        
        {/* Stats */}
        {stats && tenant?.google?.connected && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="card text-center">
              <div className="text-3xl font-bold text-arbor-500">{stats.total || 0}</div>
              <div className="text-sm text-gray-500">Total Links</div>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-green-600">{stats.active || 0}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-blue-600">{stats.accessed || 0}</div>
              <div className="text-sm text-gray-500">Accessed</div>
            </div>
            <div className="card text-center">
              <div className="text-3xl font-bold text-gray-600">{stats.completed || 0}</div>
              <div className="text-sm text-gray-500">Completed</div>
            </div>
          </div>
        )}

        {/* Data Management */}
        {tenant?.google?.connected && (
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-3">Data Management</h3>
            <p className="text-sm text-gray-600 mb-4">
              Refresh cached data to see the latest applicators, products, and equipment from Google Sheets.
            </p>
            <button
              onClick={handleRefreshCache}
              disabled={refreshing || !tenantId}
              className="btn-secondary flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh Data from Google Sheets'}
            </button>
          </div>
        )}

        {/* Standalone Form Link */}
        {tenant?.google?.connected && (
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Standalone Application Form</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Record pesticide applications not linked to a Jobber job
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/standalone/${tenantId}`
                    navigator.clipboard.writeText(url)
                    alert('Standalone form link copied to clipboard!')
                  }}
                  className="btn-secondary text-sm py-2 px-3 flex items-center gap-1"
                  title="Copy link"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </button>
                <Link
                  to={`/standalone/${tenantId}`}
                  className="btn-primary text-sm py-2 px-3 flex items-center gap-1"
                >
                  <ExternalLink className="w-4 h-4" />
                  Open Form
                </Link>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <code className="text-sm text-gray-600 break-all">
                {window.location.origin}/standalone/{tenantId}
              </code>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Share this link with field technicians for one-off applications, demo sprays, or any work not scheduled in Jobber.
            </p>
          </div>
        )}
        
        {/* Form Links */}
        {tenant?.google?.connected && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Recent Form Links</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadTenantData}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Refresh"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
                <button
                  onClick={createManualLink}
                  className="btn-secondary text-sm py-2 px-3 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Manual Link
                </button>
              </div>
            </div>
            
            {formLinks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Link2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No form links yet</p>
                <p className="text-sm mt-1">
                  Links are created automatically when new jobs are created in Jobber
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {formLinks.map(link => (
                  <div 
                    key={link.short_code}
                    className="p-4 border rounded-lg flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">
                          {link.short_code}
                        </code>
                        <span className={`status-badge ${
                          link.status === 'active' ? 'success' : 
                          link.status === 'revoked' ? 'error' : 'warning'
                        }`}>
                          {link.status}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1 truncate">
                        {link.job_number && `Job #${link.job_number}`}
                        {link.client_name && ` • ${link.client_name}`}
                      </div>
                      {link.access_count > 0 && (
                        <div className="text-xs text-gray-400 mt-1">
                          Accessed {link.access_count} time{link.access_count !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      {link.status === 'active' && (
                        <>
                          <a
                            href={`/form/${link.short_code}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-arbor-600"
                            title="Open form"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => copyLink(link.short_code)}
                            className="p-2 text-gray-400 hover:text-arbor-600"
                            title="Copy link"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => revokeLink(link.short_code)}
                            className="p-2 text-gray-400 hover:text-red-600"
                            title="Revoke"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* How it works */}
        {!tenant?.jobber?.connected && (
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">How It Works</h2>
            
            <ol className="space-y-4 text-sm">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-arbor-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                <div>
                  <div className="font-medium">Connect to Jobber</div>
                  <div className="text-gray-500">Link your Jobber account to enable automatic form creation</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-arbor-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                <div>
                  <div className="font-medium">Connect Google Sheets & Drive</div>
                  <div className="text-gray-500">Your records will be stored in a dedicated spreadsheet</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-arbor-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                <div>
                  <div className="font-medium">Automatic Form Links</div>
                  <div className="text-gray-500">A custom field with the form link is added to each new job in Jobber</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-arbor-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                <div>
                  <div className="font-medium">Field Technicians Record Applications</div>
                  <div className="text-gray-500">Mobile-friendly form captures all Oregon compliance requirements</div>
                </div>
              </li>
            </ol>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t mt-12 py-6 text-center text-sm text-gray-500">
        <p>Pesticide Application Recordkeeping System</p>
        <p className="mt-1">Oregon Compliance Ready • Arbor Ascent</p>
      </footer>
    </div>
  )
}
